/**
 * MenuPubblico (route: /m/:shortCode)
 * ====================================
 * Vista PUBBLICA del menu (sola lettura, niente login, niente registrazione).
 * Mostra:
 *   - Logo + nome ristorante
 *   - Numero tavolo
 *   - Tutte le categorie + piatti del menu attivo (combinato se più menu attivi)
 *   - Allergeni + descrizione + prezzo
 * Usata dal QR menu stampato sulle tovagliette/tavoli.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALLERGEN_LABELS = {
    glutine: "Glutine",
    lattosio: "Lattosio",
    uova: "Uova",
    pesce: "Pesce",
    crostacei: "Crostacei",
    molluschi: "Molluschi",
    arachidi: "Arachidi",
    frutta_guscio: "Frutta a guscio",
    soia: "Soia",
    sedano: "Sedano",
    senape: "Senape",
    sesamo: "Sesamo",
    solfiti: "Solfiti",
    lupini: "Lupini",
};

const MenuPubblico = () => {
    const { shortCode } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeCatId, setActiveCatId] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND}/api/public/menu/${shortCode}`);
                setData(data);
                if (data.categories && data.categories.length > 0) setActiveCatId(data.categories[0].id);
            } catch (e) {
                setError(e?.response?.data?.detail || "Tavolo non trovato o menu non disponibile.");
            } finally { setLoading(false); }
        })();
    }, [shortCode]);

    const itemsByCat = useMemo(() => {
        if (!data) return {};
        const map = {};
        for (const it of data.items) {
            if (!map[it.category_id]) map[it.category_id] = [];
            map[it.category_id].push(it);
        }
        return map;
    }, [data]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-stone-50 to-amber-50/60 text-stone-700">
            <div className="text-sm">Caricamento menu...</div>
        </div>
    );
    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
            <div className="text-center max-w-sm">
                <div className="text-6xl mb-3">🍽️</div>
                <div className="font-serif text-2xl text-stone-900 mb-1">Menu non disponibile</div>
                <div className="text-sm text-stone-600">{error}</div>
            </div>
        </div>
    );
    if (!data) return null;

    const rest = data.restaurant || {};
    const accent = rest.accent_color || "#c0a878";

    return (
        <div className="min-h-screen bg-gradient-to-b from-stone-50 via-amber-50/30 to-stone-100"
            data-testid="menu-pubblico-page"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            {/* Hero */}
            <div className="text-center pt-8 pb-6 px-4 border-b border-stone-200/80 bg-white/40 backdrop-blur-sm">
                {rest.logo_base64 && (
                    <img src={rest.logo_base64} alt={rest.name}
                        data-testid="menu-pub-logo"
                        className="mx-auto mb-3 max-h-24 object-contain" />
                )}
                <h1 className="font-serif text-3xl font-bold text-stone-900 tracking-wide" data-testid="menu-pub-restaurant">
                    {rest.name}
                </h1>
                <div className="text-xs text-stone-500 mt-3 italic">Il nostro menu del giorno</div>

                {/* Numero tavolo — grande e ben visibile */}
                <div className="mt-5 inline-flex flex-col items-center gap-1 px-8 py-4 rounded-2xl border-2 shadow-md bg-white/70"
                    data-testid="menu-pub-table-card"
                    style={{ borderColor: accent }}>
                    <div className="text-[10px] uppercase tracking-[0.4em] text-stone-500 font-bold">Tavolo</div>
                    <div className="font-serif font-bold leading-none text-stone-900" style={{ fontSize: "3.5rem", color: accent }}
                        data-testid="menu-pub-table">
                        {data.table.number}
                    </div>
                </div>
            </div>

            {/* Tabs categorie sticky */}
            {data.categories.length > 0 && (
                <div className="sticky top-0 z-10 bg-white/85 backdrop-blur-md border-b border-stone-200/80 overflow-x-auto" data-testid="menu-pub-tabs">
                    <div className="flex gap-1 px-3 py-2 min-w-max">
                        {data.categories.map((c) => (
                            <button key={c.id}
                                data-testid={`menu-pub-tab-${c.id}`}
                                onClick={() => {
                                    setActiveCatId(c.id);
                                    document.getElementById(`cat-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider font-bold whitespace-nowrap transition-colors ${
                                    activeCatId === c.id
                                        ? "bg-stone-900 text-amber-50"
                                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                                }`}>
                                {c.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Categorie + piatti */}
            <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">
                {data.categories.length === 0 ? (
                    <div className="text-center text-stone-500 py-10 italic">Nessuna categoria disponibile al momento.</div>
                ) : (
                    data.categories.map((c) => {
                        const items = itemsByCat[c.id] || [];
                        if (items.length === 0) return null;
                        return (
                            <section key={c.id} id={`cat-${c.id}`} data-testid={`menu-pub-cat-${c.id}`}>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="h-px flex-1" style={{ backgroundColor: accent }} />
                                    <h2 className="font-serif text-2xl font-bold text-stone-900 uppercase tracking-[0.15em] px-2">
                                        {c.name}
                                    </h2>
                                    <div className="h-px flex-1" style={{ backgroundColor: accent }} />
                                </div>
                                <div className="space-y-3">
                                    {items.map((it) => (
                                        <div key={it.id} className="bg-white/70 border border-stone-200/70 rounded-lg p-4 shadow-sm" data-testid={`menu-pub-item-${it.id}`}>
                                            <div className="flex items-start justify-between gap-3 mb-1">
                                                <div className="font-serif font-bold text-lg text-stone-900 flex-1">{it.name}</div>
                                                <div className="font-serif font-bold text-lg whitespace-nowrap" style={{ color: accent }}>
                                                    € {(it.price || 0).toFixed(2)}
                                                </div>
                                            </div>
                                            {it.description && (
                                                <div className="text-sm text-stone-600 italic mb-2">{it.description}</div>
                                            )}
                                            {it.allergens && it.allergens.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {it.allergens.map((a) => (
                                                        <span key={a} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200/70">
                                                            {ALLERGEN_LABELS[a] || a}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            {it.tags && it.tags.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1.5">
                                                    {it.tags.slice(0, 4).map((t) => (
                                                        <span key={t} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                                                            {t}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        );
                    })
                )}
            </div>

            <div className="text-center py-6 text-xs text-stone-400 italic">
                Per ordinare, chiama un cameriere.
                <div className="mt-1 text-[10px] opacity-70">Powered by Smart Table · Renova360</div>
            </div>
        </div>
    );
};

export default MenuPubblico;
